import React, { createContext, useContext, useEffect, useState } from "react";
import { api, recoverCsrf, setCsrfToken } from "@/lib/api";

const AuthContext = createContext(null);

/** The session is a pair of httpOnly cookies, so the browser cannot read it —
 * `/auth/me` is the single source of truth on boot. The session's CSRF token is
 * pulled into memory at the same time so writes work even where
 * `document.cookie` is unreadable (cross-site iframe / partitioned cookies). */
export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/auth/me", { _silent: true });
                setUser(data);
                recoverCsrf().catch(() => {});
            } catch (e) {
                setUser(null);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const login = async (email, password) => {
        const { data } = await api.post("/auth/login", { email, password });
        setCsrfToken(data.csrf_token);
        setUser(data.user);
        return data.user;
    };

    const logout = async () => {
        try { await api.post("/auth/logout"); } catch (e) { /* cookie is cleared either way */ }
        setUser(null);
        window.location.href = "/login";
    };

    return (
        <AuthContext.Provider value={{ user, loading, login, logout }}>
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => useContext(AuthContext);
